import { computed, effect, inject, Injectable, signal } from '@angular/core';

export type ThemePreference = 'auto' | 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly storageKey = 'eaea_theme_preference';
  readonly theme = signal<ThemePreference>(this.loadTheme());
  readonly prefersDark = signal(this.getPrefersDark());
  readonly effectiveTheme = computed<ThemePreference>(() => {
    const preference = this.theme();
    if (preference === 'auto') {
      return this.prefersDark() ? 'dark' : 'light';
    }
    return preference;
  });

  constructor() {
    if (typeof window !== 'undefined' && 'matchMedia' in window) {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      const listener = () => this.prefersDark.set(mql.matches);
      mql.addEventListener?.('change', listener);
    }

    effect(() => {
      this.persistTheme(this.theme());
    });

    effect(() => {
      this.applyTheme(this.effectiveTheme());
    });
  }

  setTheme(preference: ThemePreference): void {
    this.theme.set(preference);
  }

  private loadTheme(): ThemePreference {
    if (typeof window === 'undefined') {
      return 'auto';
    }
    const stored = window.localStorage.getItem(this.storageKey) as ThemePreference | null;
    return stored ?? 'auto';
  }

  private persistTheme(theme: ThemePreference): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(this.storageKey, theme);
  }

  private applyTheme(theme: ThemePreference): void {
    if (typeof document === 'undefined') {
      return;
    }
    const html = document.documentElement;
    html.dataset['theme'] = theme;
    html.classList.toggle('dark', theme === 'dark');
  }

  private getPrefersDark(): boolean {
    if (typeof window === 'undefined' || !('matchMedia' in window)) {
      return false;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
}
